import dagre, {
  type EdgeLabel,
  type Graph,
  type GraphLabel,
  type NodeLabel,
} from '@dagrejs/dagre';
import {
  edgeLabels,
  AUTOMATION_STEPS,
  STEP_NODE_TYPES,
} from 'pages/AutomationEngine/steps';
import { STEP_CARD_WIDTH, STEP_SLOT_HEIGHT } from '../Steps/constants';
import {
  ADD_BUTTON_HEIGHT,
  ADD_BUTTON_WIDTH,
  MERGE_HEIGHT,
} from '../addButtonConstants';
import createGraph, { listGraphItems } from '../graph';
import {
  isConditionOrGoalSplit,
  isMergeJoin,
  isBranchStep,
  isBranchSplit,
  isLoopStepNext,
} from 'pages/AutomationEngine/store/ts-utilities';
import {
  BranchStepTypes,
  type Step,
} from 'pages/AutomationEngine/types/automation';

import {
  unpositioned,
  createEdge,
  nodeFromTrigger,
  nodeFromStep,
  nodeFromBranch,
  nodeFromBranchSplit,
  isEdge,
  getMergeEnds,
  isYesStep,
  isNoStep,
  Label,
} from './layout-helpers';
import {
  CARD_EDGE_BEND_OFFSET,
  EDGE_BORDER_RADIUS,
  JUNCTION_GAP,
  NODE_TYPES,
} from './conatants';
import type { AutomationVariable } from 'pages/AutomationEngine/dialogs/VariableWizard/types';
import { Edge } from 'reactflow';
import type { CSSProperties } from 'react';
import type { FlowNode, FlowNodeType, StepSlotHeight } from './types';

type FlowGraph = Graph<GraphLabel, NodeLabel, EdgeLabel>;

const nodeFromVariable = (variable: AutomationVariable) => ({
  id: variable.id,
  type: NODE_TYPES.automationVariable,
  data: variable,
  position: unpositioned,
  className: 'nopan',
});

const nodeFromSplitBranches = (step: Step) => ({
  id: step.id,
  type: NODE_TYPES.splitBranches,
  data: step,
  position: unpositioned,
  className: 'nopan',
});

const nodeFromMergeBranches = (step: Step) => ({
  id: step.id,
  type: NODE_TYPES.joinBranches,
  data: step,
  position: unpositioned,
  className: 'nopan',
});

const getJunctionId = (id: string) => `${id}.junction`;

const isMergeSplitId = (id: string) => id.endsWith('.merge-split');

const nodeForJunction = (id: string) => ({
  id: getJunctionId(id),
  type: NODE_TYPES.junction,
  position: unpositioned,
});

const nodeForTriggerJunction = () => nodeForJunction(STEP_NODE_TYPES.TRIGGER);

const nodeForVariableJunction = () =>
  nodeForJunction(STEP_NODE_TYPES.INITIALIZE_VARIABLE);

const nodeForTwoPathJunction = ({ id }: Step, label?: Label) => ({
  id: `${getJunctionId(id)}${label ? `.${label.type}` : ''}`,
  type: NODE_TYPES.junction,
  position: unpositioned,
  data: undefined,
});

const addNodeBetwixParentandStep = (
  { id, parentKey }: Step,
  steps: Array<Step>,
  label: Label
) => {
  const parentStep = steps.find((s) => s.id === parentKey);

  // The parent's own depth is already correct for every step type. A Next/Finish
  // is stored with the depth of the loop that *encloses* it, not the one it
  // closes - addLoopDepthToSteps decrements before recording it - so taking
  // another one off here drops a position that is still inside an outer loop
  // down to 0.
  const loopDepth = parentStep?.loopDepth ?? 0;

  return {
    id: `${parentKey || STEP_NODE_TYPES.INITIALIZE_VARIABLE}.add.${id}`,
    type: NODE_TYPES.add,
    position: unpositioned,
    data: {
      leaf: false,
      branch: false,
      id,
      parentKey,
      label,
      loopDepth,
      loopVariables: parentStep?.loopVariables ?? [],
    },
  };
};

const nodeForVariableAdd = () => ({
  id: `${STEP_NODE_TYPES.INITIALIZE_VARIABLE}.add`,
  type: NODE_TYPES.add,
  position: unpositioned,
  data: {
    leaf: true,
    branch: false,
    id: null,
    parentKey: null,
    label: null,
    loopDepth: 0,
    loopVariables: [],
  },
});

const edgeFromVariableAddToStep = ({ id, parentKey }: Step) =>
  createEdge(
    `${parentKey || STEP_NODE_TYPES.INITIALIZE_VARIABLE}.add.${id}`,
    id
  );

const edgeFromParentToAdd = (
  { id, parentKey }: Step,
  label?: Label,
  goalType?: boolean,
  handle?: string
) =>
  createEdge(
    parentKey!,
    `${parentKey || STEP_NODE_TYPES.TRIGGER}.add.${id}`,
    label,
    goalType,
    undefined,
    handle
  );

const edgeFromParentToStep = (
  { id, parentKey }: Step,
  label?: Label,
  goalType?: boolean
) => createEdge(parentKey!, id, label, goalType);

const edgeFromTriggerParentToAdd = (step: StepLike, { id, parentKey }: Step) =>
  createEdge(step.id, `${parentKey || STEP_NODE_TYPES.TRIGGER}.add.${id}`);

const withCardBend = (edge: any, atTarget = false) => ({
  ...edge,
  data: {
    ...edge.data,
    bendOffset: EDGE_BORDER_RADIUS + CARD_EDGE_BEND_OFFSET,
    bendAtTarget: atTarget,
  },
});

const edgeFromVariableParentToAdd = (
  node: FlowNode,
  { id, parentKey }: Step
) => {
  const edge = createEdge(
    node.id,
    `${parentKey || STEP_NODE_TYPES.INITIALIZE_VARIABLE}.add.${id}`
  );
  return node.type === NODE_TYPES.automationVariable
    ? withCardBend(edge)
    : edge;
};

const edgeFromTriggerToJunction = ({ id }: Step) =>
  withCardBend(createEdge(id, `${STEP_NODE_TYPES.TRIGGER}.junction`));

const edgeFromVariableToJunction = ({ id }: AutomationVariable) =>
  withCardBend(
    createEdge(id, `${STEP_NODE_TYPES.INITIALIZE_VARIABLE}.junction`)
  );

// Bends right before the variable card rather than after the junction,
// so the edge runs horizontally from the junction and turns next to the card.
const edgeFromTriggerJunctionToVariables = ({ id }: AutomationVariable) =>
  withCardBend(createEdge(`${STEP_NODE_TYPES.TRIGGER}.junction`, id), true);

const edgeFromVariableToAdd = ({ id }: AutomationVariable) =>
  withCardBend(createEdge(id, `${STEP_NODE_TYPES.INITIALIZE_VARIABLE}.add`));

// used for bypassing variables when behind preview flag

const edgeFromTriggerJunctionToVariablesAdd = () =>
  createEdge(
    `${STEP_NODE_TYPES.TRIGGER}.junction`,
    `${STEP_NODE_TYPES.INITIALIZE_VARIABLE}.add`
  );

export const edgeFromSourceToTarget = (
  { id }: Pick<Step, 'id'>,
  { id: targetId }: Pick<Step, 'id'>,
  sourceHandle = 'handle-right',
  targetHandle = 'handle-left'
) =>
  createEdge(
    id,
    targetId,
    undefined,
    undefined,
    undefined,
    sourceHandle,
    targetHandle
  );

export const leafAddNodeBranchFromTrigger = () => ({
  id: `${STEP_NODE_TYPES.TRIGGER}.branch.add`,
  type: NODE_TYPES.add,
  position: unpositioned,
  data: {
    leaf: true,
    branch: true,
    id: null,
    parentKey: null,
    label: null,
    loopDepth: 0,
    loopVariables: [],
  },
});

const leafAddNode = (
  { id, loopDepth = 0, loopVariables = [] }: Step,
  label?: Label
) => ({
  id: `${id}.add${label ? `.${label.type}` : ''}`,
  type: NODE_TYPES.add,
  position: unpositioned,
  data: {
    leaf: true,
    branch: false,
    id: null,
    parentKey: id,
    label,
    loopDepth,
    loopVariables,
  },
});

const endOffBranchAddNode = (
  { id, loopDepth = 0, loopVariables = [] }: Step,
  label?: Label
) => ({
  id: `${id}.add${label ? `.${label.type}` : ''}`,
  type: NODE_TYPES.add,
  position: unpositioned,
  data: {
    leaf: false,
    endOffBranch: true,
    branch: false,
    id: null,
    parentKey: id,
    label,
    loopDepth,
    loopVariables,
  },
});

const leafFakeAddNode = ({ id }: Step, label?: Label) => ({
  id: `${id}.add${label ? `.${label.type}` : ''}`,
  type: NODE_TYPES.add,
  position: unpositioned,
  data: {
    leaf: true,
    branch: false,
    id: null,
    parentKey: id,
    label,
    fake: true,
  },
});

// line to add node
const edgeToLeafAddNode = (
  { id }: StepLike,
  label?: Label,
  goalType?: boolean,
  sourceHandle?: string,
  stroke?: string
) =>
  createEdge(
    id,
    `${id}.add${label ? `.${label.type}` : ''}`,
    label,
    goalType,
    stroke,
    sourceHandle
  );

// Edges entering a merge branches node bend right before it, so branches
// run horizontally and converge next to the merge node.
const withTargetBend = (edge: Edge) => ({
  ...edge,
  data: { ...edge.data, bendAtTarget: true },
});

const edgeToJoinBranchNode = (
  id: string,
  goalType: boolean,
  handle: string | undefined,
  parent: string,
  stroke?: string,
  style?: CSSProperties
) =>
  withTargetBend(
    createEdge(
      id,
      parent,
      undefined,
      goalType,
      stroke,
      handle,
      undefined,
      style
    )
  );

// Identifies a Loop and the Next/Finish step that closes it. Carried on the
// waypoint node's data so the post-layout passes can find both ends again.
type LoopSpan = { loopStepId: string; loopNextStepId: string };

// [loop]--[+]--[loop_next]: a waypoint between a Loop step and the Next/Finish
// step that closes it. Running the connection through a node rather than
// drawing it directly lets dagre place it in its own lane clear of the loop
// body, instead of us working out a height that dodges everything in between.
const loopSpanNode = (
  { id, loopDepth = 0, loopVariables = [] }: Step,
  loopNextStep: Step
) => ({
  id: `${id}.loop.add`,
  type: NODE_TYPES.add,
  position: unpositioned,
  data: {
    leaf: false,
    branch: false,
    id: null,
    parentKey: id,
    label: null,
    loopDepth,
    loopVariables,
    fake: true,
    // Placed by hand once dagre is done - see positionLoopSpans.
    loopSpan: { loopStepId: id, loopNextStepId: loopNextStep.id },
  },
});

// Dashed with rounded corners, so the run reads as a span across the loop
// rather than as another step-to-step connection.
const loopSpanEdgeStyle: CSSProperties = {
  strokeDasharray: '6, 4',
  strokeLinecap: 'round',
};

const asLoopSpan = (edge: any) => ({
  ...edge,
  data: {
    ...edge.data,
    loopSpan: true,
  },
});

// leaves the bottom of the Loop card
const edgeToLoopSpanNode = ({ id }: Step) =>
  asLoopSpan(
    createEdge(
      id,
      `${id}.loop.add`,
      undefined,
      false,
      undefined,
      'handle-bottom',
      undefined,
      loopSpanEdgeStyle
    )
  );

// and arrives at the bottom of the Next/Finish card
const edgeFromLoopSpanNode = (loopStep: Step, loopNextStep: Step) =>
  asLoopSpan(
    createEdge(
      `${loopStep.id}.loop.add`,
      loopNextStep.id,
      undefined,
      false,
      undefined,
      undefined,
      'handle-bottom-target',
      loopSpanEdgeStyle
    )
  );

const gotoJoinEdgeStyle: CSSProperties = {
  strokeDasharray: '6, 4',
  strokeLinecap: 'round',
  stroke: 'var(--color-feedback-warning)',
};

const edgeToJoinBranchNodeFromGoto = (
  { id }: StepLike,
  label: Label,
  goalType: boolean,
  handle: string | undefined,
  parent: string,
  stroke?: string,
  style?: CSSProperties
) =>
  createEdge(id, parent, undefined, goalType, stroke, handle, undefined, style);

// A Step, or any of the synthetic nodes (junctions, branch splits) the layout
// treats like one. Derived from Step so it stays a true supertype of it: hand
// written optionals drift from Step's nullability and stop accepting a Step.
type StepLike = Pick<Step, 'id'> &
  Partial<Pick<Step, 'loopDepth' | 'loopVariables'>>;

const junctionLeafAddNode = (
  { id, loopDepth, loopVariables }: StepLike,
  parentKey: string,
  label: Label
) => ({
  id: `${id}.add`,
  type: NODE_TYPES.add,
  position: unpositioned,
  data: {
    leaf: true,
    branch: false,
    id: null,
    parentKey: parentKey,
    label,
    loopDepth,
    loopVariables,
  },
});

const edgeFromStepToJunction = (
  { id }: Step,
  label: Label,
  goalType: boolean,
  stroke?: string,
  sourceHandle?: string
) =>
  createEdge(
    id,
    `${id}.junction${label ? `.${label.type}` : ''}`,
    label,
    goalType,
    stroke,
    sourceHandle
  );

const leafAddNodeBranchFromStep = (
  { id, loopDepth = 0, loopVariables = [] }: Step,
  branch = true
) => ({
  id: `${id}.branch.add`,
  type: NODE_TYPES.add,
  position: unpositioned,
  data: {
    leaf: true,
    branch,
    id: null,
    parentKey: id,
    label: null,
    loopDepth,
    loopVariables,
  },
});

const edgeToLeafAddNodeBranchFromStep = ({ id }: Step, branch = true) =>
  createEdge(id, `${id}.branch.add`, { leaf: true, branch, text: '' });

const leafAddNodeBranchFromVariable = () => ({
  id: `${STEP_NODE_TYPES.INITIALIZE_VARIABLE}.branch.add`,
  type: NODE_TYPES.add,
  position: unpositioned,
  data: {
    leaf: true,
    branch: true,
    id: null,
    parentKey: null,
    label: null,
    loopDepth: 0,
    loopVariables: [],
  },
});

const edgeToLeafAddNodeBranchFromVariable = (node: FlowNode) => {
  const edge = createEdge(
    node.id,
    `${STEP_NODE_TYPES.INITIALIZE_VARIABLE}.branch.add`
  );
  return node.type === NODE_TYPES.automationVariable
    ? withCardBend(edge)
    : edge;
};

// Step cards are laid out in a fixed-size slot and are vertically centered
// so their content height never
// affects the layout and they never need to be measured.
const stepSlotNodeTypes: FlowNodeType[] = [
  NODE_TYPES.automationStep,
  NODE_TYPES.branchNode,
  NODE_TYPES.connection_initialize_additional_variables,
  NODE_TYPES.connection_run_get_requests,
];

export const isStepSlotNode = (type?: FlowNodeType) =>
  !!type && stepSlotNodeTypes.includes(type);

const mergeNodeTypes: FlowNodeType[] = [
  NODE_TYPES.splitBranches,
  NODE_TYPES.joinBranches,
];

const isMergeNode = (type?: FlowNodeType) =>
  !!type && mergeNodeTypes.includes(type);

// Dimensions reported here are conservative for layout purposes.
// We need to assume max height for steps so that branches don't need
// to move when two become near each other vertically.
// `renderedHeight`/`renderedWidth` are the actual size of the node when it differs from
// the layout size, used to center the node within its layout box.
// Returns null for nodes whose size is content driven and has to be measured.
const getFixedNodeDimensions = ({ type, id }: FlowNode) => {
  if (type === NODE_TYPES.add) {
    if (id.includes('merge-split')) {
      return {
        width: ADD_BUTTON_WIDTH,
        height: ADD_BUTTON_HEIGHT + 50,
        renderedHeight: ADD_BUTTON_HEIGHT,
      };
    }
    return { width: ADD_BUTTON_WIDTH, height: ADD_BUTTON_HEIGHT };
  }
  if (isMergeNode(type)) {
    return {
      width: MERGE_HEIGHT,
      height: MERGE_HEIGHT + 40,
      renderedHeight: MERGE_HEIGHT,
    };
  }
  if (type === NODE_TYPES.branchSplit) {
    return { width: MERGE_HEIGHT, height: MERGE_HEIGHT };
  }
  if (type === NODE_TYPES.junction) {
    if (id === getJunctionId(STEP_NODE_TYPES.TRIGGER)) {
      // Widens the gap between triggers and variables, split evenly around the junction
      return {
        width: JUNCTION_GAP,
        height: 1,
        renderedWidth: 1,
      };
    }
    return { width: 1, height: 1 };
  }
  return null;
};

const getStaticNodeDimensions = (
  node: FlowNode,
  stepSlotHeight: StepSlotHeight
) =>
  isStepSlotNode(node.type)
    ? {
        width: STEP_CARD_WIDTH,
        height: stepSlotHeight,
        renderedHeight: undefined,
      }
    : getFixedNodeDimensions(node);

// Triggers, variables and a few smart connector nodes are sized by their content,
// so react-flow has to measure them before they can be laid out.
export const nodeNeedsMeasuring = (node: FlowNode) =>
  !isStepSlotNode(node.type) && !getFixedNodeDimensions(node);

// Stable reference so react-flow's memoized node wrappers don't re-render needlessly.
// The slot is mostly empty space, so it must not swallow clicks or pans; the card inside
// re-enables pointer events.
const stepSlotNodeStyles: Record<StepSlotHeight, CSSProperties> = {
  [STEP_SLOT_HEIGHT.edit]: {
    pointerEvents: 'none',
    height: STEP_SLOT_HEIGHT.edit,
  },
  [STEP_SLOT_HEIGHT.stats]: {
    pointerEvents: 'none',
    height: STEP_SLOT_HEIGHT.stats,
  },
  [STEP_SLOT_HEIGHT.history]: {
    pointerEvents: 'none',
    height: STEP_SLOT_HEIGHT.history,
  },
};

// Used for measured nodes that haven't been measured yet. React-flow keeps such
// nodes hidden until measured, and the layout is redone right after that.
const unmeasuredDimensions = {
  [STEP_SLOT_HEIGHT.edit]: {
    width: STEP_CARD_WIDTH,
    height: STEP_SLOT_HEIGHT.edit,
  },
  [STEP_SLOT_HEIGHT.stats]: {
    width: STEP_CARD_WIDTH,
    height: STEP_SLOT_HEIGHT.stats,
  },
  [STEP_SLOT_HEIGHT.history]: {
    width: STEP_CARD_WIDTH,
    height: STEP_SLOT_HEIGHT.history,
  },
};

const getNodeMaxDimensions = (
  node: FlowNode,
  stepSlotHeight: StepSlotHeight
) => {
  const { width = null, height = null } = node;
  return (
    getStaticNodeDimensions(node, stepSlotHeight) ??
    (width !== null && height !== null
      ? { width, height }
      : unmeasuredDimensions[stepSlotHeight])
  );
};

const straightEdgesAdjustment = (node: FlowNode, layoutNode: NodeLabel) => {
  const { type } = node;
  const fixedDimensions = getFixedNodeDimensions(node);
  const height = fixedDimensions
    ? fixedDimensions.renderedHeight ?? fixedDimensions.height
    : node.height;
  if (isStepSlotNode(type) || !height) {
    // Slot nodes are centered via CSS, unmeasured nodes get fixed on re-layout
    return 0;
  }
  // Regular centering, triggers with panels are offset by offsetTriggersForPanels()
  return (layoutNode.height - height) / 2;
};

export const automationToNodes = (
  {
    steps,
    triggers,
    branches,
    variables,
  }: {
    steps: Step[];
    triggers: Step[];
    branches: Record<string, string>;
    variables: AutomationVariable[];
  },
  showIntializeVariables = true
) => {
  // Setup indexes of step info for quick/convenient access later
  const leaves: Record<string, Step | null> = {};
  const stepsById: Record<string, Step> = {};
  const rootSteps: Step[] = []; // steps with no parent
  const conditionGoalJunctions: Record<string, string> = {};

  const labelsFromById = Object.keys(edgeLabels).reduce<
    Record<string, Record<string, boolean>>
  >((collect, labelType) => {
    collect[labelType] = {};
    return collect;
  }, {});

  const childrenById = steps.reduce<
    Record<string, { all: Step[]; yes: Step[]; no: Step[] }>
  >((prevSteps, currStep) => {
    const childSteps = steps.filter((s) => s.parentKey === currStep.id);
    return {
      ...prevSteps,
      [currStep.id]: {
        all: childSteps,
        yes: childSteps.filter(({ fromLabel }) => fromLabel === 'yes'),
        no: childSteps.filter(({ fromLabel }) => fromLabel === 'no'),
      },
    };
  }, {});

  const mergeEndSteps = getMergeEnds(steps, branches);

  const mergeAddIds = Object.fromEntries(
    mergeEndSteps.flatMap((mergeEnd) => {
      return mergeEnd.endNodes.map(({ sourceStep }) => [sourceStep.id, true]);
    })
  );

  // Take a single pass through steps and build the indexes
  steps.forEach((step) => {
    stepsById[step.id] = step;
    if (!step.parentKey) {
      rootSteps.push(step);
    }
    Object.keys(edgeLabels).forEach((labelType) => {
      if (step.fromLabel === labelType && step.parentKey) {
        labelsFromById[labelType][step.parentKey] = true;
      }
    });
    if (!(step.id in leaves)) {
      leaves[step.id] = step;
    }
    if (step.parentKey) {
      leaves[step.parentKey] = null;
    }
  });

  // There are two trigger layouts:
  //   - Multiple triggers
  //     - Triggers connect to Variable // leaf add button
  //   - One trigger
  //     - Trigger connects to Variable // leaf add button

  const triggerNodes = [
    ...triggers.flatMap((trigger) => {
      return [nodeFromTrigger(trigger), edgeFromTriggerToJunction(trigger)];
    }),
    nodeForTriggerJunction(),
  ];

  const variableChildrenCount =
    rootSteps.length + (branches[STEP_NODE_TYPES.INITIALIZE_VARIABLE] ? 1 : 0);

  const variableAddNode = !steps.length && nodeForVariableAdd();

  const variableJunctionNode =
    showIntializeVariables &&
    variableChildrenCount > 1 &&
    nodeForVariableJunction();

  const variableByPassParent =
    !showIntializeVariables && nodeForTriggerJunction();

  // There are five variable layouts:
  //   - Multiple a variable, no steps
  //     - Variables  connect to leaf add button
  //   - One trigger, no steps
  //     - Trigger connects to leaf add button
  //   - Multiple a variable, some steps
  //     - Variables  meet at junction, then junction connects to steps through an add button
  //     - Add button should connect to junction.
  //   - One trigger, some steps
  //     - Trigger connects to steps through leaf add button
  //     - Add button should connect to the trigger directly
  //   - One or multiple a variable, one root step
  //     - When there is exactly one root step no junction node is necessary
  //     - The root step's add button should connect to each trigger directly

  const variableNodes = showIntializeVariables
    ? [
        ...variables.flatMap((variable) => {
          return [
            edgeFromTriggerJunctionToVariables(variable),
            nodeFromVariable(variable),

            variableJunctionNode && edgeFromVariableToJunction(variable),

            variableAddNode && edgeFromVariableToAdd(variable),
          ];
        }),
        variableJunctionNode,
        variableAddNode,
      ]
    : [
        variableAddNode,
        variableAddNode && edgeFromTriggerJunctionToVariablesAdd(),
      ];

  const variableNodeParents =
    (variableAddNode && [variableAddNode]) ||
    (variableJunctionNode && [variableJunctionNode]) ||
    (variableByPassParent && [variableByPassParent]) ||
    variableNodes.filter(
      (node) =>
        node &&
        (node.type === NODE_TYPES.automationVariable ||
          node.type === NODE_TYPES.automationStep)
    );

  const hasYesStep = (step: Step) =>
    !!labelsFromById[edgeLabels.yes.type][step.id];

  const hasNoStep = (step: Step) =>
    !!labelsFromById[edgeLabels.no.type][step.id];

  // Ensure yes steps come before no steps for the same condition.
  // TODO it may make sense to eventually move this ordering up
  // into the store during move/insert/delete.
  steps = listGraphItems(createGraph(steps));

  // we want do any merge branches first as we need to
  const prioritizedSteps = [...steps].sort((a, b) => {
    if (a.type === BranchStepTypes.MERGE) {
      if (b.type === BranchStepTypes.MERGE) {
        return 0;
      }
      return -1;
    }
    if (b.type === BranchStepTypes.MERGE) {
      return 1;
    }

    return 0;
  });

  const isParentToGoToStep = (step: Step) =>
    steps.filter((node) => {
      return (
        step.parentKey === node.id &&
        node.type === AUTOMATION_STEPS.go_to_automation_step.type
      );
    });

  let parentBranchLength: Record<string, { yes: number; no: number }> = {};

  const stepNodes = prioritizedSteps.flatMap((step) => {
    let label = null;
    const befores: (FlowNode | Edge)[] = [];
    const afters: (FlowNode | Edge)[] = [];
    const parent = stepsById[step.parentKey!];
    const conditionYesChildren: (Step | null)[] = [];
    const conditionNoChildren: (Step | null)[] = [];
    const noJunction = nodeForTwoPathJunction(step, edgeLabels.no);

    const yesJunction = nodeForTwoPathJunction(step, edgeLabels.yes);

    const branchId = Object.keys(branches).find((key) => key === step.id);

    const isLeaf = leaves[step.id];

    if (isConditionOrGoalSplit(step)) {
      // we need to know how much condition/goal step has children
      steps.forEach((item) => {
        if (item.parentKey === step.id) {
          if (item.fromLabel === edgeLabels.yes.type) {
            conditionYesChildren.push(item);
          } else {
            conditionNoChildren.push(item);
          }
        }
      });

      if (branchId) {
        // we need this to create a leaf with an add button from an existing branch of goal(condition): parent--y(n)--[j]--add
        if (branches[branchId] === edgeLabels.yes.type) {
          conditionYesChildren.push(null);
          afters.push(
            junctionLeafAddNode(yesJunction, step.id, edgeLabels.yes),
            edgeToLeafAddNode(yesJunction)
          );
        }
        if (branches[branchId] === edgeLabels.no.type) {
          conditionNoChildren.push(null);
          befores.push(
            junctionLeafAddNode(noJunction, step.id, edgeLabels.no),
            edgeToLeafAddNode(noJunction)
          );
        }
        parentBranchLength = {
          ...parentBranchLength,
          [step.id]: {
            yes: conditionYesChildren.length,
            no: conditionNoChildren.length,
          },
        };
      }
    }

    // if condition/goal has more than 1 child on "yes" or "no" path - we create a junction for them
    if (conditionYesChildren.length > 1 || conditionNoChildren.length > 1) {
      // we save id junction for each child for creating edge parent--y(n)--[j]--child in the future
      conditionYesChildren.forEach((item) => {
        item &&
          (conditionGoalJunctions[item.id] =
            `${step.id}.junction.${item.fromLabel}`);
      });

      conditionNoChildren.forEach((item) => {
        item &&
          (conditionGoalJunctions[item.id] =
            `${step.id}.junction.${item.fromLabel}`);
      });

      if (conditionYesChildren.length) {
        befores.push(
          yesJunction,
          isConditionOrGoalSplit(step)
            ? edgeFromStepToJunction(
                step,
                edgeLabels.yes,
                step?.goalType,
                undefined,
                'handle-top'
              )
            : edgeFromStepToJunction(step, edgeLabels.yes, step?.goalType)
        );
      } else if (conditionNoChildren.length > 1) {
        befores.push(
          yesJunction,
          isConditionOrGoalSplit(step)
            ? edgeFromStepToJunction(
                step,
                edgeLabels.yes,
                step?.goalType,
                undefined,
                'handle-top'
              )
            : edgeFromStepToJunction(step, edgeLabels.yes, step?.goalType),
          junctionLeafAddNode(yesJunction, step.id, edgeLabels.yes),
          edgeToLeafAddNode(yesJunction)
        );
      }

      if (conditionNoChildren.length) {
        afters.push(
          noJunction,
          isConditionOrGoalSplit(step)
            ? edgeFromStepToJunction(
                step,
                edgeLabels.no,
                step?.goalType,
                undefined,
                'handle-bottom'
              )
            : edgeFromStepToJunction(step, edgeLabels.no, step?.goalType)
        );
      } else if (conditionYesChildren.length > 1) {
        afters.push(
          noJunction,
          isConditionOrGoalSplit(step)
            ? edgeFromStepToJunction(
                step,
                edgeLabels.no,
                step?.goalType,
                undefined,
                'handle-bottom'
              )
            : edgeFromStepToJunction(step, edgeLabels.no, step?.goalType),
          junctionLeafAddNode(noJunction, step.id, edgeLabels.no),
          edgeToLeafAddNode(noJunction)
        );
      }
    }

    if (conditionGoalJunctions[step.id]) {
      // creating edge [j]--(+)
      befores.push(
        edgeFromTriggerParentToAdd(
          {
            id: conditionGoalJunctions[step.id],
          },
          step
        )
      );
    }
    if (isYesStep(step)) {
      label = edgeLabels.yes;
      // only the No Add
      if (parent && !hasNoStep(parent) && !conditionGoalJunctions[step.id]) {
        afters.push(
          // leafAddNode(parent, edgeLabels.no),
          endOffBranchAddNode(parent, edgeLabels.no),
          edgeToLeafAddNode(
            parent,
            edgeLabels.no,
            parent?.goalType,
            'handle-bottom'
          )
        );
      }
    }
    if (isNoStep(step)) {
      label = edgeLabels.no;
      // only the Yes Add
      if (parent && !hasYesStep(parent) && !conditionGoalJunctions[step.id]) {
        befores.push(
          endOffBranchAddNode(parent, edgeLabels.yes),
          edgeToLeafAddNode(
            parent,
            edgeLabels.yes,
            parent?.goalType,
            'handle-top'
          )
        );
      }
    }

    if (isLeaf) {
      if (isConditionOrGoalSplit(step)) {
        // merge steps leaves have two add nodes: one for yes and one for no
        // Condition steps leaves have two add nodes: one for yes and one for no

        befores.push(
          leafAddNode(step, edgeLabels.yes),
          edgeToLeafAddNode(step, edgeLabels.yes, step?.goalType, 'handle-top')
        );

        afters.push(
          leafAddNode(step, edgeLabels.no),
          edgeToLeafAddNode(
            step,
            edgeLabels.no,
            step?.goalType,
            'handle-bottom'
          )
        );
      } else if (
        !(
          step.type === AUTOMATION_STEPS.go_to_automation_step.type ||
          isBranchStep(step)
        )
      ) {
        if (mergeAddIds[step.id]) {
          afters.push(endOffBranchAddNode(step), edgeToLeafAddNode(step));
        } else {
          afters.push(leafAddNode(step), edgeToLeafAddNode(step));
        }
      }
    }

    const nodeForStep = isConditionOrGoalSplit(step)
      ? nodeFromSplitBranches(step)
      : isMergeJoin(step)
        ? nodeFromMergeBranches(step)
        : isBranchStep(step)
          ? nodeFromBranch(step)
          : isBranchSplit(step)
            ? nodeFromBranchSplit(step)
            : nodeFromStep(step);

    const shouldHideEdgeBeforeStep = (parent: StepLike) => {
      if (parentBranchLength[parent?.id]) {
        return true;
      }

      if (!isConditionOrGoalSplit(parent)) {
        return false;
      }

      return (
        childrenById[parent?.id]?.yes?.length > 1 ||
        childrenById[parent?.id]?.no?.length > 1
      );
    };

    const edgeBeforeAddNodeBetweenParentAndStep = shouldHideEdgeBeforeStep(
      parent
    )
      ? false
      : (!conditionGoalJunctions[step.id] && isConditionOrGoalSplit(step)) ||
          isMergeJoin(step) ||
          isBranchSplit(step)
        ? null
        : isConditionOrGoalSplit(parent)
          ? step.parentKey &&
            edgeFromParentToAdd(
              step,
              label,
              parent?.goalType,
              step?.fromLabel === 'yes' ? 'handle-top' : 'handle-bottom'
            )
          : step.parentKey &&
            edgeFromParentToAdd(step, label, parent?.goalType);

    const isBetweenParentAndStep =
      !isConditionOrGoalSplit(step) &&
      !isMergeJoin(step) &&
      !isBranchSplit(step);

    const addNodeBetweenParentAndStep = !isParentToGoToStep(step).length
      ? isBetweenParentAndStep && addNodeBetwixParentandStep(step, steps, label)
      : null;

    const edgeAfterAddNodeBetweenParentAndStep =
      isBetweenParentAndStep && edgeFromVariableAddToStep(step);

    // if the step is a merge split or branch split add the edge back to it's parent
    const edgeFromParentToMerge =
      (isConditionOrGoalSplit(step) || isBranchSplit(step)) &&
      edgeFromParentToStep(step, label, parent?.goalType);

    const edgeVariablesToAdd = step.parentKey
      ? []
      : variableNodeParents.map((parentEntity) =>
          edgeFromVariableParentToAdd(parentEntity, step)
        );

    return [
      ...befores,
      nodeForStep,
      addNodeBetweenParentAndStep,
      edgeAfterAddNodeBetweenParentAndStep,
      edgeBeforeAddNodeBetweenParentAndStep,
      edgeFromParentToMerge,
      ...edgeVariablesToAdd,
      ...afters,
    ];
  });

  const branchNodes = Object.keys(branches).flatMap((parentKey) => {
    const step = stepsById[parentKey];
    if (!step) {
      return [];
    }
    if (parentKey === STEP_NODE_TYPES.INITIALIZE_VARIABLE) {
      const [variableNodeParent] = variableNodeParents;
      if (!variableNodeParent) {
        return [];
      }
      return [
        leafAddNodeBranchFromVariable(),
        edgeToLeafAddNodeBranchFromVariable(variableNodeParent),
      ];
    }
    if (isConditionOrGoalSplit(step)) {
      return []; // we handle branching of condition/split and goal steps in stepNodes
    }

    return [
      leafAddNodeBranchFromStep(step),
      edgeToLeafAddNodeBranchFromStep(step),
    ];
  });

  // Dashed goto edges are collected separately so they can be rendered last,
  // keeping them on top of any overlapping solid edges.
  const gotoJoinEdges: Edge[] = [];

  const joinEdges = mergeEndSteps.reduce<(FlowNode | Edge)[]>(
    (prevMerge, currMerge) => {
      const { endNodes, join } = currMerge;
      if (!join) {
        return prevMerge;
      }
      const edges = endNodes.flatMap((node) => {
        const { sourceStep, branches } = node;
        const nodeEdges = branches.flatMap<FlowNode | Edge>((branchName) => {
          let label = undefined;

          switch (branchName) {
            case 'yes':
              label = edgeLabels.yes;
              break;

            case 'no':
              label = edgeLabels.no;
              break;

            default:
              break;
          }

          if (
            'type' in sourceStep &&
            sourceStep.type === 'go_to_automation_step'
          ) {
            const fakeAddId = `${sourceStep.id}.add`;

            gotoJoinEdges.push(
              edgeToJoinBranchNodeFromGoto(
                sourceStep,
                label || null,
                sourceStep.goalType,
                undefined,
                fakeAddId,
                undefined,
                gotoJoinEdgeStyle
              ),
              withTargetBend(
                edgeToJoinBranchNodeFromGoto(
                  { id: fakeAddId },
                  label || null,
                  sourceStep.goalType,
                  undefined,
                  join.id,
                  undefined,
                  gotoJoinEdgeStyle
                )
              )
            );

            return [leafFakeAddNode(sourceStep)];
          }

          const myJunctionId = `${sourceStep.id}.junction${label ? `.${label.type}` : ''}.add`;
          const myId = stepNodes.some((s) => s?.id === myJunctionId)
            ? myJunctionId
            : `${sourceStep.id}.add${label ? `.${label.type}` : ''}`;

          return edgeToJoinBranchNode(
            myId,
            'goalType' in sourceStep ? sourceStep.goalType : false,
            undefined,
            join.id,
            undefined,
            undefined
          );
        });
        return nodeEdges;
      });

      return [...prevMerge, ...edges];
    },
    []
  );
  // A Next/Finish step carries the id of the loop it closes on loopParent. Only
  // span loops whose step is on the canvas, so the edge can never dangle.
  const stepNodeIds = new Set(stepNodes.map((node) => node?.id));

  const loopEdges = steps.flatMap((step) => {
    const loopStep =
      isLoopStepNext(step) && step.loopParent
        ? stepsById[step.loopParent]
        : undefined;
    return loopStep && stepNodeIds.has(loopStep.id)
      ? [
          loopSpanNode(loopStep, step),
          edgeToLoopSpanNode(loopStep),
          edgeFromLoopSpanNode(loopStep, step),
        ]
      : [];
  });

  const nodesReturned = [
    // Additional --[+] for bonus branches: these go first intentionally
    ...branchNodes,
    // [trigger] or [triggers]---[junction] or [trigger(s)]---[+] (no steps)
    ...triggerNodes,
    // [variable] or [variable]---[junction] or [variable(s)]---[+] (no steps)
    ...variableNodes,
    // ---[+]---[step], opt. with additional --[+] for leaves
    ...stepNodes,
    // [endStep]--[join_branch]
    ...joinEdges,
    // [goto]--[fake +]--[join_branch] dashed edges, rendered last to stay on top
    ...gotoJoinEdges,
    // [loop]--[loop_next]
    ...loopEdges,
  ].reduce(
    (collect, node) => {
      if (!node) {
        return collect;
      }
      const [nodes, edges] = collect;
      if (!isEdge(node)) {
        return [nodes.concat(node), edges];
      }
      // Edges leaving a split bend right before their target, so the paths
      // fan out horizontally from the split and turn next to the next node.
      return [
        nodes,
        edges.concat(
          isMergeSplitId(node.source) ? withCardBend(node, true) : node
        ),
      ];
    },
    [[], []]
  );

  return nodesReturned;
};

// Dagre doesn't guarantee that the triggers and variables columns share a center,
// so the trigger junction and the junction from variables to steps can end up on
// different heights. Re-center both columns (and the trigger junction) on the node
// the variables flow into, so both junctions lie on the same horizontal axis.

const alignEntryColumns = (graph: FlowGraph, nodes: FlowNode[]) => {
  const triggerJunctionId = getJunctionId(STEP_NODE_TYPES.TRIGGER);
  if (!graph.hasNode(triggerJunctionId)) {
    return;
  }
  const idsOfType = (type: FlowNodeType) =>
    nodes.filter((node) => node.type === type).map(({ id }) => id);
  const triggerIds = idsOfType(NODE_TYPES.automationTrigger);
  const variableIds = idsOfType(NODE_TYPES.automationVariable);

  const getColumnCenter = (ids: string[]) => {
    const boxes = ids.reduce<NodeLabel[]>((acc, id) => {
      const node = graph.node(id);
      if (node) {
        acc.push(node);
      }
      return acc;
    }, []);
    const top = Math.min(...boxes.map(({ y = 0, height }) => y - height / 2));
    const bottom = Math.max(
      ...boxes.map(({ y = 0, height }) => y + height / 2)
    );
    return (top + bottom) / 2;
  };

  // The y of the single node the given nodes connect to, if there is exactly one
  const getSharedTargetY = (ids: string[]) => {
    const targets = new Set(ids.flatMap((id) => graph.successors(id) || []));
    return targets.size === 1 ? graph.node([...targets][0])?.y ?? null : null;
  };

  const axis = variableIds.length
    ? getSharedTargetY(variableIds) ?? getColumnCenter(variableIds)
    : getSharedTargetY([triggerJunctionId]) ??
      graph.node(triggerJunctionId)?.y ??
      0;

  const centerColumn = (ids: string[]) => {
    if (!ids.length) {
      return;
    }
    const offset = axis - getColumnCenter(ids);
    ids.forEach((id) => {
      const layoutNode = graph.node(id);
      if (layoutNode) {
        layoutNode.y = (layoutNode.y ?? 0) + offset;
      }
    });
  };

  centerColumn(triggerIds);
  centerColumn(variableIds);
  const triggerJunction = graph.node(triggerJunctionId);
  if (triggerJunction) {
    triggerJunction.y = axis;
  }
};

// Horizontal gap between connected nodes, same as dagre's default rank separation
const NODE_GAP_X = 50;

// Nodes of these types are stacked in a column and stay centered within it
const columnNodeTypes: FlowNodeType[] = [
  NODE_TYPES.automationTrigger,
  NODE_TYPES.automationVariable,
];

type CompactionBox = {
  id: string;
  type?: FlowNodeType;
  left: number;
  width: number;
  top: number;
  bottom: number;
};

// Dagre lays nodes out in ranks (columns) as wide as their widest node and pushes nodes
// as far right as their successors allow. So an add button sharing a rank with a step card
// of another branch gets surrounded by empty space, and the steps of a short branch that
// merges with a longer one end up right-aligned against the merge node.
// Shift every node left as far as its incoming edges and the nodes before it at the
// same height allow, keeping dagre's vertical placement and left to right order.
const compactHorizontally = (graph: FlowGraph, nodes: FlowNode[]) => {
  const boxes = nodes.reduce<CompactionBox[]>((acc, { id, type }) => {
    const layoutNode = graph.node(id);
    if (layoutNode) {
      const { x = 0, y = 0, width, height } = layoutNode;
      acc.push({
        id,
        type,
        left: x - width / 2,
        width,
        top: y - height / 2,
        bottom: y + height / 2,
      });
    }
    return acc;
  }, []);
  boxes.sort((a, b) => a.left - b.left);

  const placed: CompactionBox[] = [];
  const placedById: Record<string, CompactionBox> = {};

  // The leftmost position the node can take, or null when nothing constrains it
  const getMinLeft = (box: CompactionBox) => {
    const blockers = [
      ...(graph.predecessors(box.id) || []).flatMap((id) =>
        placedById[id] ? [placedById[id]] : []
      ),
      ...placed.filter(
        ({ top, bottom }) => top < box.bottom && box.top < bottom
      ),
    ];
    return blockers.length
      ? Math.max(
          ...blockers.map(({ left, width }) => left + width + NODE_GAP_X)
        )
      : null;
  };

  const getShift = (box: CompactionBox) => {
    const minLeft = getMinLeft(box);
    return minLeft === null ? 0 : Math.max(0, box.left - minLeft);
  };

  const place = (box: CompactionBox, shift: number) => {
    box.left -= shift;
    placed.push(box);
    placedById[box.id] = box;
  };

  boxes.forEach((box) => {
    if (placedById[box.id]) {
      return;
    }
    if (box.type && columnNodeTypes.includes(box.type)) {
      // Shift the whole column at once so its nodes stay centered to each other
      const column = boxes.filter(({ type }) => type === box.type);
      const shift = Math.min(...column.map(getShift));
      column.forEach((member) => place(member, shift));
      return;
    }
    place(box, getShift(box));
  });

  placed.forEach(({ id, left, width }) => {
    const layoutNode = graph.node(id);
    if (layoutNode) {
      layoutNode.x = left + width / 2;
    }
  });
};

// Vertical gap between nodes, same as dagre's default node separation
const NODE_GAP_Y = 50;

type Box = { left: number; right: number; top: number; bottom: number };

const boxesIntersect = (a: Box, b: Box) =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

// Leaf add buttons display inset, closer to the node before them. Except for the one
// variables connect to when there are no steps yet: edges from multiple variables
// converge on it and need the full gap to bend, same as when steps follow.
const isInsetLeaf = (node?: FlowNode) =>
  node?.type === NODE_TYPES.add &&
  node.data?.leaf &&
  !node.data?.branch &&
  node.id !== nodeForVariableAdd().id;

type NodeMove = { ids: string[]; offset: number };

const shiftNodes = (graph: FlowGraph, moves: NodeMove[], sign: 1 | -1 = 1) =>
  moves.forEach(({ ids, offset }) =>
    ids.forEach((id) => {
      const layoutNode = graph.node(id);
      if (layoutNode) {
        layoutNode.y = (layoutNode.y ?? 0) + sign * offset;
      }
    })
  );

// Applies vertical moves to laid out nodes, reverting them if the moved nodes would
// overlap any other node or edge. Returns whether the moves were kept.
const createVerticalMover = (
  graph: FlowGraph,
  nodes: FlowNode[],
  edges: Edge[]
) => {
  const nodesById: Record<string, FlowNode> = Object.fromEntries(
    nodes.map((node) => [node.id, node])
  );

  const getBox = (id: string): Box => {
    const layoutNode = graph.node(id);
    if (layoutNode) {
      const { x = 0, y = 0, width, height } = layoutNode;
      const inset = isInsetLeaf(nodesById[id]) ? ADD_BUTTON_WIDTH : 0;
      return {
        left: x - width / 2 - inset,
        right: x + width / 2,
        top: y - height / 2,
        bottom: y + height / 2,
      };
    } else {
      return { left: 0, right: 0, top: 0, bottom: 0 };
    }
  };

  // Edges are drawn as steps, bending next to the source unless told otherwise,
  // so their long horizontal run lies at the target's (or the source's) height.
  const getEdgeRun = ({ source, target, data }: Edge): Box | null => {
    const sourceNode = graph.node(source);
    const targetNode = graph.node(target);
    if (!sourceNode || !targetNode) {
      return null;
    }
    const left = (sourceNode.x ?? 0) + sourceNode.width / 2;
    const right = (targetNode.x ?? 0) - targetNode.width / 2;
    const y = (data?.bendAtTarget ? sourceNode.y : targetNode.y) ?? 0;
    return left < right ? { left, right, top: y, bottom: y } : null;
  };

  const isInTheWay = (moved: string[]) => {
    const movedIds = new Set(moved);
    const movedBoxes = moved.map(getBox);
    const otherBoxes = nodes
      .filter(({ id }) => !movedIds.has(id))
      .map(({ id }) => getBox(id));
    return (
      movedBoxes.some((box) =>
        otherBoxes.some((other) => boxesIntersect(box, other))
      ) ||
      edges.some((edge) => {
        const run = getEdgeRun(edge);
        if (!run) {
          return false;
        }
        const touchesMoved =
          movedIds.has(edge.source) || movedIds.has(edge.target);
        return (touchesMoved ? otherBoxes : movedBoxes).some((box) =>
          boxesIntersect(run, box)
        );
      })
    );
  };

  // Each move is checked on its own once all are applied, so moved nodes
  // can't end up in each other's way either
  const tryMoves = (moves: NodeMove[]) => {
    shiftNodes(graph, moves);
    if (moves.some(({ ids }) => isInTheWay(ids))) {
      shiftNodes(graph, moves, -1);
      return false;
    }
    return true;
  };

  return { getBox, tryMoves };
};

// Dagre keeps nodes of the same rank apart vertically, so a leaf add button of a condition
// or goal split gets pushed above/below every step card of the other path within its rank,
// even though the button sits left of those cards. Move such buttons (and the junction in
// front of them, if any) back next to the split whenever nothing is in the way.
const alignSplitLeaves = (
  graph: FlowGraph,
  nodes: FlowNode[],
  edges: Edge[]
) => {
  const nodesById: Record<string, FlowNode> = Object.fromEntries(
    nodes.map((node) => [node.id, node])
  );
  const { tryMoves } = createVerticalMover(graph, nodes, edges);
  const movesBySplit: Record<string, NodeMove[]> = {};

  nodes.forEach((node) => {
    const labelType = node.data?.label?.type;
    if (
      !isInsetLeaf(node) ||
      (labelType !== edgeLabels.yes.type && labelType !== edgeLabels.no.type)
    ) {
      return;
    }
    const ids = [node.id];
    let [parentId, ...otherParents] = graph.predecessors(node.id) || [];
    if (
      parentId &&
      nodesById[parentId]?.type === NODE_TYPES.junction &&
      graph.successors(parentId)?.length === 1
    ) {
      ids.unshift(parentId);
      [parentId, ...otherParents] = graph.predecessors(parentId) || [];
    }
    if (
      !parentId ||
      otherParents.length ||
      nodesById[parentId]?.type !== NODE_TYPES.splitBranches
    ) {
      return;
    }

    const split = graph.node(parentId);
    const leaf = graph.node(node.id);
    if (!split || !leaf) {
      return;
    }
    const direction = labelType === edgeLabels.yes.type ? -1 : 1;
    const offset =
      (split.y ?? 0) +
      (direction * (leaf.height + NODE_GAP_Y)) / 2 -
      (leaf.y ?? 0);
    if (Math.abs(offset) >= 1) {
      movesBySplit[parentId] = [
        ...(movesBySplit[parentId] || []),
        { ids, offset },
      ];
    }
  });

  // Both leaves of a split are moved at once, as they may be in each other's way,
  // then one by one if together they don't fit
  Object.values(movesBySplit).forEach((moves) => {
    if (!tryMoves(moves) && moves.length > 1) {
      moves.forEach((move) => tryMoves([move]));
    }
  });
};

// Dagre doesn't keep the yes and no paths of a condition or goal split symmetric, e.g. when
// the split comes right after merged branches, one path gets pushed far away from it.
// Move both paths (everything reachable only from each of them) vertically, so they start
// at the same distance from the split, as close to it as they fit next to each other,
// whenever nothing else is in the way.
const balanceSplitPaths = (
  graph: FlowGraph,
  nodes: FlowNode[],
  edges: Edge[]
) => {
  const nodesById: Record<string, FlowNode> = Object.fromEntries(
    nodes.map((node) => [node.id, node])
  );
  const { getBox, tryMoves } = createVerticalMover(graph, nodes, edges);

  // How much closer the paths can get to each other, keeping the usual gap
  // between any of their nodes that lie above one another
  const getSlack = (yesIds: string[], noIds: string[]) => {
    const yesBoxes = yesIds.map(getBox);
    const noBoxes = noIds.map(getBox);
    return yesBoxes.reduce(
      (slack, yesBox) =>
        noBoxes.reduce(
          (min, noBox) =>
            yesBox.left < noBox.right && noBox.left < yesBox.right
              ? Math.min(min, noBox.top - yesBox.bottom - NODE_GAP_Y)
              : min,
          slack
        ),
      Infinity
    );
  };

  const getReachable = (startId: string) => {
    const reachable = new Set<string>();
    const queue = [startId];
    while (queue.length) {
      const id = queue.shift()!;
      if (!reachable.has(id)) {
        reachable.add(id);
        queue.push(...(graph.successors(id) || []));
      }
    }
    return reachable;
  };

  const getPathStart = (splitId: string, labelType: string) =>
    edges.find(
      ({ source, data }) => source === splitId && data?.type === labelType
    )?.target;

  // Inner splits lie right of the outer ones and are balanced first,
  // so balancing an outer split moves them as a whole
  const splits = nodes
    .filter(({ type }) => type === NODE_TYPES.splitBranches)
    .map(({ id }) => ({ id, layoutNode: graph.node(id) }))
    .filter(({ layoutNode }) => layoutNode)
    .sort((a, b) => (b.layoutNode.x ?? 0) - (a.layoutNode.x ?? 0));

  splits.forEach(({ id: splitId, layoutNode: split }) => {
    const yesId = getPathStart(splitId, edgeLabels.yes.type);
    const noId = getPathStart(splitId, edgeLabels.no.type);
    // Leaf add buttons next to the split are handled by alignSplitLeaves()
    if (
      !yesId ||
      !noId ||
      isInsetLeaf(nodesById[yesId]) ||
      isInsetLeaf(nodesById[noId])
    ) {
      return;
    }
    const yesStart = graph.node(yesId);
    const noStart = graph.node(noId);
    if (!yesStart || !noStart) {
      return;
    }
    const splitY = split.y ?? 0;
    const yesDistance = splitY - (yesStart.y ?? 0);
    const noDistance = (noStart.y ?? 0) - splitY;
    const yesReachable = getReachable(yesId);
    const noReachable = getReachable(noId);
    // Nodes reachable from both paths come after they merge and stay in place
    const yesIds = [...yesReachable].filter((id) => !noReachable.has(id));
    const noIds = [...noReachable].filter((id) => !yesReachable.has(id));

    const slack = Math.max(0, getSlack(yesIds, noIds));
    // Never further than the paths already are, at least far enough for the
    // first nodes of both paths to clear the split
    const distance = Math.max(
      (yesDistance + noDistance - slack) / 2,
      (yesStart.height + NODE_GAP_Y) / 2,
      (noStart.height + NODE_GAP_Y) / 2
    );
    if (
      Math.abs(yesDistance - distance) < 1 &&
      Math.abs(noDistance - distance) < 1
    ) {
      return;
    }

    // Positive offsets move down
    const yesMove = { ids: yesIds, offset: yesDistance - distance };
    const noMove = { ids: noIds, offset: distance - noDistance };
    if (tryMoves([yesMove, noMove])) {
      return;
    }
    // Otherwise only balance them, moving the path closer to the split away from it
    const farther = Math.max(yesDistance, noDistance);
    tryMoves([
      yesDistance < farther
        ? { ids: yesIds, offset: yesDistance - farther }
        : { ids: noIds, offset: farther - noDistance },
    ]);
  });
};

// The steps between a Loop and the Next/Finish that closes it, add buttons and
// junctions included. Walks the tree rather than reading loop data off the
// nodes, and ignores the span legs so it stays inside the loop body.
const loopBodyIds = (
  { loopStepId, loopNextStepId }: LoopSpan,
  edges: Edge[]
) => {
  const body = new Set<string>();
  const queue = edges
    .filter((edge) => edge.source === loopStepId && !edge.data?.loopSpan)
    .map((edge) => edge.target);

  while (queue.length) {
    const id = queue.shift();
    if (!id || id === loopNextStepId || body.has(id)) {
      continue;
    }
    body.add(id);
    edges.forEach((edge) => {
      if (edge.source === id && !edge.data?.loopSpan) {
        queue.push(edge.target);
      }
    });
  }

  return body;
};

// Dagre centres a node between parallel paths, so a Loop ends up halfway
// between its body and the span running beneath it - which lifts the body off
// the line the rest of the automation sits on. Keeping the span in the graph is
// what reserves the corridor it runs through, so it stays there and the body is
// dropped back onto the Loop's line afterwards.
const straightenLoopSpans = (
  graph: FlowGraph,
  nodes: FlowNode[],
  edges: Edge[]
) => {
  nodes.forEach((node) => {
    const span: LoopSpan | undefined = node.data?.loopSpan;
    if (!span) {
      return;
    }

    const loop = graph.node(span.loopStepId);
    const firstInBody = edges.find(
      (edge) => edge.source === span.loopStepId && !edge.data?.loopSpan
    )?.target;
    const firstNode = firstInBody ? graph.node(firstInBody) : undefined;

    if (!loop || !firstNode) {
      return;
    }

    const drop = (loop.y ?? 0) - (firstNode.y ?? 0);
    if (!drop) {
      return;
    }

    loopBodyIds(span, edges).forEach((id) => {
      const target = graph.node(id);
      if (target) {
        target.y = (target.y ?? 0) + drop;
      }
    });
  });
};

// Dagre is free to route a span either side of the line it runs alongside, and
// for a short one it picks above - which draws the dotted line back through the
// cards. Place every span by hand instead: 20px of clearance under the lowest
// point of everything the loop contains, inner spans included, so a nested loop
// never crosses a card or another span.
const LOOP_SPAN_CLEARANCE = 20;

const positionLoopSpans = (
  graph: FlowGraph,
  nodes: FlowNode[],
  edges: Edge[]
) => {
  // Dagre reserves a conservative height for cards, well over what most of them
  // render at, and a node's rendered centre is the y dagre gave it. Measure the
  // clearance against what the user actually sees.
  const renderedHeight = (id: string) =>
    nodes.find((node) => node.id === id)?.height ?? graph.node(id)?.height ?? 0;

  const bottomOf = (id: string) => {
    const node = graph.node(id);
    return node ? (node.y ?? 0) + renderedHeight(id) / 2 : -Infinity;
  };

  const spans = nodes
    .filter((node) => node.data?.loopSpan)
    .map((node) => ({
      node,
      span: node.data.loopSpan as LoopSpan,
      body: loopBodyIds(node.data.loopSpan, edges),
    }))
    // Innermost first - a loop that contains another has the larger body - so
    // an outer span measures against inner spans that are already placed.
    .sort((a, b) => a.body.size - b.body.size);

  spans.forEach(({ node, span, body }, i) => {
    const { loopStepId, loopNextStepId } = span;
    const spanNode = graph.node(node.id);

    if (!spanNode) {
      return;
    }

    const inner = spans
      .slice(0, i)
      .filter(({ span: other }) => body.has(other.loopStepId))
      .map(({ node: other }) => other.id);

    const bottom = [loopStepId, loopNextStepId, ...body, ...inner].reduce(
      (lowest, id) => Math.max(lowest, bottomOf(id)),
      -Infinity
    );

    if (!Number.isFinite(bottom)) {
      return;
    }

    spanNode.y = bottom + LOOP_SPAN_CLEARANCE + renderedHeight(node.id) / 2;

    makeRoomUnderSpan(
      graph,
      nodes,
      edges,
      span,
      new Set([node.id, loopStepId, loopNextStepId, ...body, ...inner]),
      spanNode.y + renderedHeight(node.id) / 2 + LOOP_SPAN_CLEARANCE,
      renderedHeight
    );
  });
};

// Dagre only reserves a span's corridor in the one column its waypoint sits
// in, and nested spans stack lower than that corridor allows. So the dotted
// line can run through whatever dagre packed beneath the loop - typically the
// next branch down. Push what lies beneath back down until it clears, moving
// only what sits right of the point where its path and the loop's split, so
// the trigger column and the split itself stay where they are.
const makeRoomUnderSpan = (
  graph: FlowGraph,
  nodes: FlowNode[],
  edges: Edge[],
  { loopStepId, loopNextStepId }: LoopSpan,
  region: Set<string>,
  clearBelow: number,
  renderedHeight: (id: string) => number
) => {
  const loop = graph.node(loopStepId);
  const loopNext = graph.node(loopNextStepId);

  if (!loop || !loopNext) {
    return;
  }

  const left = (loop.x ?? 0) - loop.width / 2;
  const right = (loopNext.x ?? 0) + loopNext.width / 2;
  const top = (id: string) =>
    (graph.node(id)?.y ?? 0) - renderedHeight(id) / 2;

  const colliders = nodes
    .map(({ id }) => id)
    .filter((id) => {
      const other = graph.node(id);
      if (!other || region.has(id)) {
        return false;
      }
      const x = other.x ?? 0;
      return (
        x + other.width / 2 > left &&
        x - other.width / 2 < right &&
        (other.y ?? 0) > (loop.y ?? 0) &&
        top(id) < clearBelow
      );
    });

  if (!colliders.length) {
    return;
  }

  const delta = clearBelow - Math.min(...colliders.map(top));
  // Everything on the colliding row moves together, so measure the row by its
  // centre rather than by tops that differ with each node's height.
  const rowY = Math.min(...colliders.map((id) => graph.node(id)?.y ?? 0));

  const parents = edges.reduce<Record<string, string[]>>((acc, edge) => {
    if (!edge.data?.loopSpan) {
      (acc[edge.target] ??= []).push(edge.source);
    }
    return acc;
  }, {});

  const ancestorsOf = (id: string) => {
    const seen = new Set<string>();
    const queue = [...(parents[id] ?? [])];
    while (queue.length) {
      const current = queue.shift()!;
      if (!seen.has(current)) {
        seen.add(current);
        queue.push(...(parents[current] ?? []));
      }
    }
    return seen;
  };

  // The split the loop's path and the colliding path both come from: the
  // right-most ancestor they share.
  const loopAncestors = ancestorsOf(loopStepId);
  const forkX = colliders
    .flatMap((id) => [...ancestorsOf(id)])
    .filter((id) => loopAncestors.has(id))
    .reduce(
      (max, id) => Math.max(max, graph.node(id)?.x ?? -Infinity),
      -Infinity
    );

  nodes.forEach(({ id }) => {
    const other = graph.node(id);
    if (
      other &&
      !region.has(id) &&
      (other.x ?? 0) > forkX &&
      (other.y ?? 0) >= rowY
    ) {
      other.y = (other.y ?? 0) + delta;
    }
  });
};

export const layoutNodes = (
  nodes: FlowNode[] = [],
  edges: Edge[] = [],
  stepSlotHeight: StepSlotHeight
): FlowNode[] => {
  const graph = new dagre.graphlib.Graph<GraphLabel, NodeLabel, EdgeLabel>();

  graph.setGraph({
    marginx: 0,
    marginy: 0,
    rankdir: 'LR', // Tree orients itself left to right,
  });

  graph.setDefaultEdgeLabel(() => ({}));

  let trigger: FlowNode | undefined;
  let triggers = 0;
  const joinIds = new Set<string>();
  nodes.forEach((node) => {
    graph.setNode(node.id, getNodeMaxDimensions(node, stepSlotHeight));
    if (node.type === NODE_TYPES.automationTrigger) {
      triggers += 1;
      trigger = node;
    }
    if (node.type === NODE_TYPES.joinBranches) {
      joinIds.add(node.id);
    }
  });

  edges.forEach((edge) => {
    // Dagre stretches the cheapest edges of a short branch that merges with a longer one.
    // Making edges into merge nodes the cheapest keeps the short branch's steps next to
    // where it starts (left aligned) instead of next to the merge node, which also lets
    // dagre reserve vertical space for them there.
    graph.setEdge(edge.source, edge.target, {
      weight: joinIds.has(edge.target) ? 1 : 2,
    });
  });

  if (trigger && triggers === 1) {
    // When there is a single trigger, lay it out so that it will
    // be aligned with the first step card added to it.  This ensures
    // there is no jarring y-transition when the first step is added.
    //
    // This is very similar but not identical to the condition in getExtent().
    // The difference is that this affects visual layout, whereas the other
    // just affects the perceived x/y extent of all the nodes.  In getExtent()
    // we want there to be room in the extent for the panel when it opens, and
    // here we only want to do so when it doesn't affect the visuals (when there
    //  is one trigger): we don't want to expand the gap between trigger nodes.
    const triggerNode = graph.node(trigger.id);
    if (triggerNode) {
      triggerNode.height = Math.max(triggerNode.height, stepSlotHeight);
    }
  }

  dagre.layout(graph, { disableOptimalOrderHeuristic: true });

  // Undoes dagre's centring of a Loop against its own span before the passes
  // below read any y values.
  straightenLoopSpans(graph, nodes, edges);

  alignEntryColumns(graph, nodes);
  compactHorizontally(graph, nodes);
  alignSplitLeaves(graph, nodes, edges);
  balanceSplitPaths(graph, nodes, edges);

  // Last: a span is placed clear of everything beneath it, so it has to measure
  // against positions nothing will move again.
  positionLoopSpans(graph, nodes, edges);

  const layoutedNodes = nodes.reduce<FlowNode[]>((acc, node) => {
    const layoutNode = graph.node(node.id);
    if (layoutNode) {
      const adjustX = isInsetLeaf(node) ? -ADD_BUTTON_WIDTH : 0;
      const adjustY = straightEdgesAdjustment(node, layoutNode);
      const staticDimensions = getStaticNodeDimensions(node, stepSlotHeight);
      const width = staticDimensions?.renderedWidth ?? layoutNode.width;
      acc.push({
        ...node,
        // Measured nodes keep their measured size (or none yet), so that react-flow's
        // own measurement stays the source of truth for them.
        ...(staticDimensions && {
          width,
          height: layoutNode.height,
        }),
        ...(isStepSlotNode(node.type) && {
          style: stepSlotNodeStyles[stepSlotHeight],
        }),
        position: {
          // The position from dagre layout is the center of the node.
          // Calculating the position of the top left corner for rendering.
          x: (layoutNode.x ?? 0) - width / 2 + adjustX,
          y: (layoutNode.y ?? 0) - layoutNode.height / 2 + adjustY,
        },
      });
    }
    return acc;
  }, []);

  return layoutedNodes;
};

export const nodeHasDimension = (node: FlowNode) => {
  const { width = null, height = null } = node;
  return width !== null && height !== null;
};

export const nodeHasPosition = (node: FlowNode) => {
  const {
    position: { x, y },
  } = node;
  return x !== unpositioned.x && y !== unpositioned.y;
};

export const getExtent = (els: FlowNode[], stepSlotHeight: StepSlotHeight) => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let degenerate = true;
  let trigger: FlowNode | undefined;

  els.forEach((node) => {
    if (!isEdge(node) && nodeHasPosition(node) && nodeHasDimension(node)) {
      const {
        position: { x, y },
      } = node;
      const { width, height } = getNodeMaxDimensions(node, stepSlotHeight);
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x + width);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y + height);
      degenerate = false;
      if (node.type === NODE_TYPES.automationTrigger) {
        trigger = node;
      }
    }
  });
  if (trigger) {
    // Always ensure bottom trigger has room to open its panel without affecting the extent.
    maxY = Math.max(maxY, trigger.position.y + stepSlotHeight);
  }
  if (degenerate) {
    return [
      [0, 0],
      [0, 0],
    ];
  }
  return [
    [minX, minY],
    [maxX, maxY],
  ];
};
